import {subWeeks} from "date-fns";
import {useEffect, useRef, useState} from "react";
import ReportUtils from "../../../utils/report-utils.js";
import DispatchByPlaceService from "../../../services/reports/dispatch-by-place.js";
import {useGlobalKey} from "../../../hooks/use-global-key.js";
import SubScroll from "../../../components/sub-scroll/sub-scroll.jsx";
import {Template} from "../../../components/template/template.jsx";
import HeaderButtons from "../../../components/reports/report-menu/header-buttons.jsx";
import Menu from "../../../components/reports/report-menu/menu.jsx";
import Filters from "./components/filters.jsx";
import AutoDatetime from "../../../components/reports/auto-datetime/auto-datetime.jsx";
import {Card} from "../../../components/card/card.jsx";
import NotAppliedContent from "../../../components/reports/report-menu/not-applied-content.jsx";
import NoDataContent from "../../../components/reports/report-menu/no-data-content.jsx";
import Content from "./components/content.jsx";

const initialFilter = () => ({
    dtStart:  subWeeks(new Date(), 1),
    dtEnd: new Date(),
    placeType: [],
    place: [],
    nature: [],
});

const filtersLabels = {
    dtStart: "Data/hora inicial",
    dtEnd: "Data/hora final",
    placeType: "Tipo de local",
    place: "Local pré-cadastrado",
    nature: "Natureza",
};

export default function DispatchByPlace() {
    const [isLoading, setIsLoading] = useState(false);
    const [collapsed, setCollapsed] = useState(false);
    const [filters, setFilters] = useState(initialFilter);
    const [fixedTop, setFixedTop] = useState(null);
    const [reportData, setReportData] = useState(null);

    const [tableColumns, setTableColumns] = useState(() => ({
        place: {label: "Local pré-cadastrado", visible: true},
        street: {label: "Logradouro", visible: true},
        neighborhood: {label: "Bairro", visible: true},
        sector: {label: "Setor", visible: true},
        quantity: {label: "Quantidade de despachos", visible: true}
    }));

    const [headerSelected, setHeaderSelected] = useState(-1);
    const printComponentRef = useRef(null);
    const buttonRef = useRef(null);
    const title = "Despacho por local";

    useEffect(() => {
        if (!reportData) return;
        ReportUtils.appendFilters(
            ReportUtils.filters({
                ...filters,
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : "",
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : "",
            }, filtersLabels),
        );
    }, [reportData]);

    const apply = async () => {
        ReportUtils.applyLoad({
            setLoading: setIsLoading,
            clearContent: () => {
                setReportData(null)
            }
        });

        try {
            const periodValidation = ReportUtils.periodValidate(filters.dtStart, filters.dtEnd, null, null);

            if (!periodValidation.ok) {
                ReportUtils.alertErrorMessage(periodValidation.message);
                setIsLoading(false);
                return;
            };

            const formattedFilters = {
                dtStart: filters.dtStart ? ReportUtils.formatDateTime(filters.dtStart) : null,
                dtEnd: filters.dtEnd ? ReportUtils.formatDateTime(filters.dtEnd) : null,
                placeType: filters.placeType?.map(i => i.value) ?? [],
                place: filters.place?.map(i => i.value) ?? [],
                nature: filters.nature?.map(i => i.value) ?? [],
            };

            const auditFilters = ReportUtils.filters({
                ...filters,
                dtStart: formattedFilters.dtStart,
                dtEnd: formattedFilters.dtEnd,
            }, filtersLabels, true);

            const response = await DispatchByPlaceService.apply({ ...formattedFilters, filters: auditFilters });
            setReportData(response?.data ?? []);

            const scale = window.visualViewport?.scale ?? 1;
            if (window.innerWidth <= 1440 || scale > 1)  setCollapsed(true);

        } catch (error) {
            ReportUtils.alertErrorMessage("Erro ao gerar relatório.");
            console.error("Erro ao gerar o relatório", error);
        } finally {
            setIsLoading(false);
        }
    };

    const clearSearch = () => {
        setFilters(initialFilter());
        setReportData(null);
    };

    const toggleFilters = () => {
        if (!collapsed && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setFixedTop(rect.top);
        }  ;
        setCollapsed(prev => !prev);
    };

    useGlobalKey("Enter", apply);

    return (
        <SubScroll dependency={[collapsed, reportData]}>
            <div className="flex">
                <Template isLoading={isLoading}>
                    <Template.Header>
                        <Template.Title>{title}</Template.Title>
                        <HeaderButtons
                            exportable={true}
                            exportInSheets={true}
                            printable={true}
                            headerChange={true}
                            visibilityColumns={true}
                            columns={tableColumns}
                            setColumns={setTableColumns}
                            printComponentRef={printComponentRef}
                            tableColumns={tableColumns}
                            reportData={reportData}
                            data={reportData?.length}
                            headerSelected={headerSelected}
                            setHeaderSelected={setHeaderSelected}
                        />
                    </Template.Header>
                    <Template.Body className={`${collapsed ? "p-0!" : ""}`}>
                        <Menu
                            buttonRef={buttonRef}
                            fixedTop={fixedTop}
                            collapsed={collapsed}
                            toggleFilters={toggleFilters}
                            clearSearch={clearSearch}
                            apply={apply}
                        >
                            <Filters filters={filters} setFilters={setFilters} />
                            <AutoDatetime setFilters={setFilters} />
                        </Menu>
                    </Template.Body>
                </Template>
                <Card className={`w-full h-fit mr-2 ${!reportData || reportData?.length === 0 ? "mt-[59px]" : "mt-[61px]"} print:!mt-[-15px] transform ${!reportData || reportData?.length === 0 ? "!bg-[#f3f3f4] !border-none !-translate-y-[5px]" : ""} ${collapsed ? "ml-2" : ""}`}>
                    <Card.Body>
                        <div className={`flex-1 ${!reportData || reportData?.length === 0 ? "p-[0]" : "px-[20px] py-[5px]"}`}>
                            <NotAppliedContent reportData={reportData} />
                            <NoDataContent reportData={reportData} data={reportData?.length} />
                            <Content
                                reportData={reportData}
                                data={reportData?.length}
                                entity={headerSelected}
                                title={title}
                                printComponentRef={printComponentRef}
                                setIsLoading={setIsLoading}
                                tableColumns={tableColumns}
                            />
                        </div>
                    </Card.Body>
                </Card>
            </div>
        </SubScroll>
    )
}